// -----------------------------------------------------------------------
// ProductionScheduleDialog — owner spec 2026-07-02
// -----------------------------------------------------------------------
// A per-production planning canvas. Opens as a full-screen dialog from the
// "לו״ז + ציוד" pill. Structure:
//   Day 1
//     ├── date + label (editable)
//     ├── Section: ציוד צילום ותאורה  → contact + notes + items grid
//     ├── Section: ציוד מחנה
//     ├── Section: ארוחת בוקר
//     ├── Section: ארוחת צהריים
//     └── Section: ארוחת ערב
//   Day 2 …
// The whole tree is persisted with a single PUT
// /api/fullframe/productions/{id}/schedule.
// -----------------------------------------------------------------------
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, X as XIcon, Calendar as CalendarIcon,
  UserSquare, Phone, MessageCircle, Mail, Loader2, Save,
  Send, Check, ListChecks, Clipboard,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { updateProductionSchedule } from "./api";

// -------- utils -----------------------------------------------------------
const uuid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const telHref = (p) => (onlyDigits(p) ? `tel:${onlyDigits(p)}` : null);
const waHref = (p) => {
  const d = onlyDigits(p);
  if (!d) return null;
  const intl = d.startsWith("0") ? `972${d.slice(1)}` : d;
  return `https://wa.me/${intl}`;
};
const mailHref = (e) => (e ? `mailto:${e}` : null);

// Owner spec 2026-07-08b — Hebrew weekday label derived from ISO date.
const hebWeekday = (iso) => {
  if (!iso) return "";
  const parts = (iso || "").split("-").map((p) => parseInt(p, 10));
  if (parts.length < 3 || parts.some(isNaN)) return "";
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  if (isNaN(d.getTime())) return "";
  const names = ["יום א׳", "יום ב׳", "יום ג׳", "יום ד׳", "יום ה׳", "יום ו׳", "שבת"];
  return names[d.getUTCDay()] || "";
};

const DEFAULT_SECTION_TYPES = [
  { type: "equipment_lighting", name: "ציוד צילום ותאורה" },
  { type: "camp_equipment",     name: "ציוד מחנה" },
  { type: "breakfast",          name: "ארוחת בוקר" },
  { type: "lunch",              name: "ארוחת צהריים" },
  { type: "dinner",             name: "ארוחת ערב" },
];

// Owner spec 2026-07-02b — bold, distinct colour theme per category so
// the SA can visually parse the day at a glance. Order below matches
// the order returned by DEFAULT_SECTION_TYPES.
const SECTION_THEMES = {
  equipment_lighting: {
    border: "border-amber-400/60",
    bg: "bg-amber-500/10",
    headerBg: "bg-amber-500/25",
    text: "text-amber-100",
    accent: "text-amber-300",
    emoji: "🎥",
  },
  camp_equipment: {
    border: "border-orange-400/60",
    bg: "bg-orange-500/10",
    headerBg: "bg-orange-500/25",
    text: "text-orange-100",
    accent: "text-orange-300",
    emoji: "🏕️",
  },
  breakfast: {
    border: "border-sky-400/60",
    bg: "bg-sky-500/10",
    headerBg: "bg-sky-500/25",
    text: "text-sky-100",
    accent: "text-sky-300",
    emoji: "🌅",
  },
  lunch: {
    border: "border-emerald-400/60",
    bg: "bg-emerald-500/10",
    headerBg: "bg-emerald-500/25",
    text: "text-emerald-100",
    accent: "text-emerald-300",
    emoji: "🍽️",
  },
  dinner: {
    border: "border-fuchsia-400/60",
    bg: "bg-fuchsia-500/10",
    headerBg: "bg-fuchsia-500/25",
    text: "text-fuchsia-100",
    accent: "text-fuchsia-300",
    emoji: "🍷",
  },
  custom: {
    border: "border-white/15",
    bg: "bg-stone-900/70",
    headerBg: "bg-stone-800/70",
    text: "text-stone-100",
    accent: "text-stone-300",
    emoji: "📋",
  },
};
const themeFor = (type) => SECTION_THEMES[type] || SECTION_THEMES.custom;

const newSection = (defaults = {}) => ({
  id: uuid(),
  type: defaults.type || "custom",
  name: defaults.name || "",
  notes: "",
  contact_name: "",
  contact_phone: "",
  contact_whatsapp: "",
  contact_email: "",
  items: [],
});

const newDay = () => ({
  id: uuid(),
  date: "",
  label: "",
  sections: DEFAULT_SECTION_TYPES.map((t) => newSection(t)),
});

const newItem = () => ({ id: uuid(), name: "", quantity: "", note: "", confirmed: false });

// Owner spec 2026-07-02c — parse free-form Hebrew equipment lists into
// structured rows. Handles: linebreaks, commas, "N X שם", "Nx שם" and
// bare lines without a leading number (e.g. "טילטה מיראז׳").
function parseNotesToItems(text) {
  if (!text) return [];
  return text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    // Drop lines that are clearly free-form notes rather than an item.
    // Anything starting with "הערה:" / "הערה" is treated as commentary.
    .filter((line) => !/^הערה[:\s]/.test(line))
    .map((line) => {
      // Optional leading digits (with optional trailing "x") + rest.
      const m = line.match(/^(\d+)\s*x?\s*(.+)$/);
      if (m) {
        return { id: uuid(), name: m[2].trim(), quantity: m[1], note: "", confirmed: false };
      }
      return { id: uuid(), name: line, quantity: "", note: "", confirmed: false };
    });
}

// -------- main dialog -----------------------------------------------------
export function ProductionScheduleDialog({ open, onOpenChange, production, companyId, onSaved }) {
  const [days, setDays] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [contactFor, setContactFor] = useState(null);
  const initialSchedule = production?.schedule;

  // Hydrate from prod.schedule whenever the dialog opens. If the
  // production has no schedule yet, seed with one blank day so the SA
  // doesn't stare at an empty dialog.
  // Owner spec 2026-07-08g — hydrate ONLY on open (per production). Deps
  // used to include `initialSchedule`, which changes reference on every
  // successful auto-save refetch and wiped the SA's in-flight edits.
  useEffect(() => {
    if (!open) return;
    if (Array.isArray(initialSchedule) && initialSchedule.length > 0) {
      setDays(JSON.parse(JSON.stringify(initialSchedule)));
    } else {
      setDays([newDay()]);
    }
    setSaveStatus("idle");
  }, [open, production?.id]);

  // Owner spec 2026-07-07e — debounced auto-save, same pattern as
  // ProductionCrewDialog. Skips the initial hydration.
  // Owner spec 2026-07-08i — skip no-op saves triggered by parent
  // re-renders (unstable `onSaved` reference).
  const hydrated = useRef(false);
  const lastSavedRef = useRef("");
  useEffect(() => {
    if (!open) { hydrated.current = false; lastSavedRef.current = ""; return; }
    if (!production?.id) return;
    const currentJson = JSON.stringify(days);
    if (!hydrated.current) {
      hydrated.current = true;
      lastSavedRef.current = currentJson;
      return;
    }
    if (currentJson === lastSavedRef.current) return;
    const t = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await updateProductionSchedule(production.id, days, companyId);
        lastSavedRef.current = currentJson;
        setSaveStatus("saved");
        onSaved && onSaved();
      } catch (e) {
        setSaveStatus("error");
        toast.error(e.response?.data?.detail || "השמירה האוטומטית נכשלה");
      }
    }, 700);
    return () => clearTimeout(t);
  }, [days, open, production?.id, companyId, onSaved]);

  // Owner spec 2026-07-08h — brief 600ms flash then GONE.
  useEffect(() => {
    if (saveStatus !== "saved") return;
    const t = setTimeout(() => setSaveStatus("idle"), 600);
    return () => clearTimeout(t);
  }, [saveStatus]);

  const totalItems = useMemo(
    () => days.reduce((s, d) => s + d.sections.reduce((ss, sec) => ss + (sec.items?.length || 0), 0), 0),
    [days]
  );

  // ---- mutators (all update a specific path immutably) --------------------
  const patchDay = (dayId, patch) =>
    setDays((prev) => prev.map((d) => (d.id === dayId ? { ...d, ...patch } : d)));
  const patchSection = (dayId, secId, patch) =>
    setDays((prev) => prev.map((d) => d.id !== dayId ? d : {
      ...d,
      sections: d.sections.map((s) => s.id === secId ? { ...s, ...patch } : s),
    }));
  const patchItem = (dayId, secId, itemId, patch) =>
    setDays((prev) => prev.map((d) => d.id !== dayId ? d : {
      ...d,
      sections: d.sections.map((s) => s.id !== secId ? s : {
        ...s,
        items: s.items.map((it) => it.id === itemId ? { ...it, ...patch } : it),
      }),
    }));
  const setSectionItems = (dayId, secId, items) =>
    setDays((prev) => prev.map((d) => d.id !== dayId ? d : {
      ...d,
      sections: d.sections.map((s) => s.id === secId ? { ...s, items } : s),
    }));

  const addDay = () => setDays((prev) => [...prev, newDay()]);
  const deleteDay = (dayId) => {
    if (!window.confirm("למחוק את היום?")) return;
    setDays((prev) => prev.filter((d) => d.id !== dayId));
  };
  const addItem = (dayId, secId) =>
    patchSection(dayId, secId, { items: [...(days.find((d) => d.id === dayId)?.sections.find((s) => s.id === secId)?.items || []), newItem()] });
  const deleteItem = (dayId, secId, itemId) => {
    setDays((prev) => prev.map((d) => d.id !== dayId ? d : {
      ...d,
      sections: d.sections.map((s) => s.id !== secId ? s : {
        ...s,
        items: s.items.filter((it) => it.id !== itemId),
      }),
    }));
  };
  const addSection = (dayId) =>
    patchDay(dayId, { sections: [...(days.find((d) => d.id === dayId)?.sections || []), newSection({ name: "קטגוריה חדשה" })] });
  const deleteSection = (dayId, secId) => {
    if (!window.confirm("למחוק את הקטגוריה?")) return;
    setDays((prev) => prev.map((d) => d.id !== dayId ? d : {
      ...d, sections: d.sections.filter((s) => s.id !== secId),
    }));
  };

  const save = async () => { /* deprecated — auto-save handles this now */ };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir="rtl"
        className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-5xl max-h-[92vh] overflow-y-auto"
        data-testid="ff-schedule-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-pink-400" />
            <span className="bg-gradient-to-l from-pink-400 to-fuchsia-400 bg-clip-text text-transparent">
              לו״ז + ציוד הפקה
            </span>
            {production?.name && (
              <span className="text-xs text-stone-400 font-normal">· {production.name}</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            {days.length} ימים · {totalItems} פריטים
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          {days.map((day, idx) => (
            <DayCard
              key={day.id}
              day={day}
              idx={idx}
              prodName={production?.name}
              onPatchDay={(patch) => patchDay(day.id, patch)}
              onDeleteDay={() => deleteDay(day.id)}
              onPatchSection={(secId, patch) => patchSection(day.id, secId, patch)}
              onDeleteSection={(secId) => deleteSection(day.id, secId)}
              onAddSection={() => addSection(day.id)}
              onPatchItem={(secId, itemId, patch) => patchItem(day.id, secId, itemId, patch)}
              onSetSectionItems={(secId, items) => setSectionItems(day.id, secId, items)}
              onAddItem={(secId) => addItem(day.id, secId)}
              onDeleteItem={(secId, itemId) => deleteItem(day.id, secId, itemId)}
              onOpenContact={(sec) => setContactFor({ day, sec })}
            />
          ))}
          <button
            type="button"
            onClick={addDay}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-white/20 bg-white/5 hover:bg-white/10 text-white text-sm font-bold py-3 transition"
            data-testid="ff-schedule-add-day"
          >
            <Plus className="h-4 w-4" />
            הוסף יום
          </button>

          <div className="flex items-center justify-between gap-2 pt-2 sticky bottom-0 bg-stone-900/95 backdrop-blur border-t border-white/10 py-2">
            {/* Owner spec 2026-07-08h — completely silent when idle. */}
            <div className="flex items-center gap-2 text-xs font-bold min-h-[20px]" data-testid="ff-schedule-autosave-status">
              {saveStatus === "saving" && (<><Loader2 className="h-4 w-4 animate-spin text-amber-400" /><span className="text-amber-200">שומר…</span></>)}
              {saveStatus === "saved"  && (<><Save className="h-4 w-4 text-emerald-400" /><span className="text-emerald-200">נשמר ✓</span></>)}
              {saveStatus === "error"  && (<span className="text-rose-300">שמירה נכשלה — ננסה שוב בשינוי הבא</span>)}
            </div>
            <Button onClick={() => onOpenChange(false)} className="bg-stone-700 hover:bg-stone-600 text-white font-bold" data-testid="ff-schedule-close">
              סגור
            </Button>
          </div>
        </div>

        {/* Contact sub-popup */}
        <ContactPopup
          data={contactFor}
          onClose={() => setContactFor(null)}
          onSave={(patch) => {
            if (contactFor) patchSection(contactFor.day.id, contactFor.sec.id, patch);
            setContactFor(null);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

// -------- Day Card --------------------------------------------------------
function DayCard({ day, idx, prodName, onPatchDay, onDeleteDay, onPatchSection, onDeleteSection, onAddSection, onPatchItem, onSetSectionItems, onAddItem, onDeleteItem, onOpenContact }) {
  // Owner spec 2026-07-06 — bolder visual seam between days so the SA
  // can scan a long schedule at a glance. Rotate through 4 accent tones
  // so consecutive days never look identical.
  const dayAccents = [
    "border-pink-500/60 bg-pink-950/60",
    "border-fuchsia-500/60 bg-fuchsia-950/60",
    "border-indigo-500/60 bg-indigo-950/60",
    "border-cyan-500/60 bg-cyan-950/60",
  ];
  const dayHeaderAccents = [
    "from-pink-600 to-rose-500",
    "from-fuchsia-600 to-purple-500",
    "from-indigo-600 to-blue-500",
    "from-cyan-600 to-teal-500",
  ];
  const wrapCls = dayAccents[idx % dayAccents.length];
  const headerCls = dayHeaderAccents[idx % dayHeaderAccents.length];
  return (
    <div className={`rounded-2xl border-4 ${wrapCls} shadow-xl shadow-black/40 overflow-hidden`} data-testid={`ff-schedule-day-${day.id}`}>
      {/* Big coloured stripe with day number + date so the seam between
          days is impossible to miss. */}
      <div className={`px-4 py-3 flex items-center gap-3 flex-wrap bg-gradient-to-l ${headerCls} text-white`}>
        <span className="text-2xl font-extrabold leading-none tracking-tight shrink-0" data-testid={`ff-schedule-day-num-${day.id}`}>
          יום {idx + 1}
        </span>
        <div className="h-6 w-px bg-white/40" />
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            value={day.date}
            onChange={(e) => onPatchDay({ date: e.target.value })}
            className="w-40 bg-black/25 border-white/30 text-white font-bold"
            dir="ltr"
            data-testid={`ff-schedule-day-date-${day.id}`}
          />
          {/* Owner spec 2026-07-08b — auto-computed weekday under the date. */}
          {hebWeekday(day.date) && (
            <div className="text-[11px] font-extrabold text-white/95 text-center bg-black/25 rounded px-1 py-0.5" data-testid={`ff-schedule-day-weekday-${day.id}`}>
              {hebWeekday(day.date)}
            </div>
          )}
        </div>
        <Input
          placeholder="תווית (יום א', יום ב'…)"
          value={day.label}
          onChange={(e) => onPatchDay({ label: e.target.value })}
          className="flex-1 min-w-[160px] bg-black/25 border-white/30 text-white font-bold placeholder:text-white/60"
          data-testid={`ff-schedule-day-label-${day.id}`}
        />
        <button
          type="button"
          onClick={onDeleteDay}
          className="text-white/90 hover:text-white hover:bg-black/30 rounded-lg p-1.5 transition"
          data-testid={`ff-schedule-delete-day-${day.id}`}
          aria-label="מחק יום"
          title="מחק יום"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {day.sections.map((sec) => (
          <SectionCard
            key={sec.id}
            section={sec}
            dayLabel={day.label}
            dayDate={day.date}
            prodName={prodName}
            onPatch={(patch) => onPatchSection(sec.id, patch)}
            onDelete={() => onDeleteSection(sec.id)}
            onOpenContact={() => onOpenContact(sec)}
            onPatchItem={(itemId, patch) => onPatchItem(sec.id, itemId, patch)}
            onSetItems={(items) => onSetSectionItems(sec.id, items)}
            onAddItem={() => onAddItem(sec.id)}
            onDeleteItem={(itemId) => onDeleteItem(sec.id, itemId)}
          />
        ))}
        <button
          type="button"
          onClick={onAddSection}
          className="w-full inline-flex items-center justify-center gap-1 rounded-lg border border-dashed border-white/25 bg-white/5 hover:bg-white/10 text-stone-200 text-[11px] font-bold py-1.5 transition"
          data-testid={`ff-schedule-add-section-${day.id}`}
        >
          <Plus className="h-3.5 w-3.5" />
          הוסף קטגוריה
        </button>
      </div>
    </div>
  );
}

// -------- Section Card ----------------------------------------------------
function SectionCard({ section, dayLabel, dayDate, prodName, onPatch, onDelete, onOpenContact, onPatchItem, onAddItem, onDeleteItem, onSetItems }) {
  const hasContact =
    !!(section.contact_phone || section.contact_whatsapp || section.contact_email || section.contact_name);
  const theme = themeFor(section.type);
  const [sendOpen, setSendOpen] = useState(false);

  // Parse the current notes into item rows and append them (dedup by lower-case name).
  const runParse = () => {
    const parsed = parseNotesToItems(section.notes);
    if (parsed.length === 0) {
      toast.info("לא נמצאו פריטים לפירוק בהערות");
      return;
    }
    const existingNames = new Set((section.items || []).map((it) => (it.name || "").trim().toLowerCase()));
    const additions = parsed.filter((p) => !existingNames.has(p.name.trim().toLowerCase()));
    onSetItems([...(section.items || []), ...additions]);
    toast.success(`נוספו ${additions.length} פריטים`);
  };

  return (
    <div className={`rounded-xl border-2 ${theme.border} ${theme.bg} overflow-hidden`} data-testid={`ff-schedule-section-${section.id}`}>
      {/* Coloured header strip — LEFT: send-to-supplier button + delete;
          RIGHT (RTL): emoji + name + contact button. */}
      <div className={`flex items-center gap-2 flex-wrap px-3 py-2 ${theme.headerBg} border-b ${theme.border}`}>
        {/* Left cluster (in RTL these show on the LEFT edge — first in DOM) */}
        <button
          type="button"
          onClick={() => setSendOpen(true)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold border border-white/40 bg-black/40 text-white hover:bg-black/60 transition"
          data-testid={`ff-schedule-section-send-${section.id}`}
          title="שלח לספק ב-WhatsApp / מייל"
        >
          <Send className="h-3.5 w-3.5" />
          שלח לספק
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/25 rounded-lg p-1 transition"
          aria-label="מחק קטגוריה"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
        <div className="flex-1" />
        <span className="text-lg leading-none" aria-hidden>{theme.emoji}</span>
        <Input
          value={section.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder="שם הקטגוריה"
          className={`min-w-[140px] max-w-xs bg-black/25 border-white/10 ${theme.text} font-extrabold text-base`}
          data-testid={`ff-schedule-section-name-${section.id}`}
        />
        <button
          type="button"
          onClick={onOpenContact}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold border transition ${hasContact ? "border-white/40 bg-black/40 text-white hover:bg-black/60" : "border-white/20 bg-black/20 text-white/80 hover:bg-black/40"}`}
          data-testid={`ff-schedule-section-contact-${section.id}`}
        >
          <UserSquare className="h-3.5 w-3.5" />
          {hasContact ? (section.contact_name || "איש קשר") : "הוסף איש קשר"}
        </button>
      </div>

      <div className="p-3 space-y-2">
        <Textarea
          value={section.notes}
          onChange={(e) => onPatch({ notes: e.target.value })}
          placeholder="הערות / רשימת ציוד חופשית — אפשר גם להדביק מהגיליון"
          className={`bg-stone-900/70 border-white/10 ${theme.text} text-sm min-h-[64px]`}
          data-testid={`ff-schedule-section-notes-${section.id}`}
        />

        {section.notes && section.notes.trim() && (
          <button
            type="button"
            onClick={runParse}
            className={`inline-flex items-center gap-1 rounded-lg border border-white/25 bg-black/30 hover:bg-black/50 ${theme.text} text-[11px] font-bold px-2 py-1 transition`}
            data-testid={`ff-schedule-section-parse-${section.id}`}
            title="פרק את ההערות לשורות פריט מסודרות"
          >
            <ListChecks className="h-3.5 w-3.5" />
            פרק לפריטים
          </button>
        )}

        {section.items?.length > 0 && (
          <div className="space-y-1.5">
            <div className="grid grid-cols-[24px,1fr,80px,1fr,24px] gap-2 text-[10px] uppercase tracking-widest font-bold text-white/60 px-1">
              <div />
              <div>שם מוצר</div>
              <div className="text-center">כמות</div>
              <div>הערה</div>
              <div />
            </div>
            {section.items.map((it) => (
              <div key={it.id} className="grid grid-cols-[24px,1fr,80px,1fr,24px] gap-2 items-center" data-testid={`ff-schedule-item-${it.id}`}>
                <button
                  type="button"
                  onClick={() => onPatchItem(it.id, { confirmed: !it.confirmed })}
                  className={`h-6 w-6 rounded-md border flex items-center justify-center transition ${it.confirmed ? "bg-emerald-500 border-emerald-400 text-white" : "bg-black/30 border-white/25 text-white/40 hover:border-white/60"}`}
                  title={it.confirmed ? "מאושר — לחץ לביטול" : "אשר פריט (הוזמן / הובא)"}
                  data-testid={`ff-schedule-item-confirm-${it.id}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <Input
                  value={it.name}
                  onChange={(e) => onPatchItem(it.id, { name: e.target.value })}
                  placeholder="שם מוצר"
                  className={`bg-stone-900/70 border-white/10 ${theme.text} h-8 text-sm ${it.confirmed ? "line-through opacity-60" : ""}`}
                />
                <Input
                  value={it.quantity}
                  onChange={(e) => onPatchItem(it.id, { quantity: e.target.value })}
                  placeholder="כמות"
                  className={`bg-stone-900/70 border-white/10 ${theme.text} h-8 text-sm text-center ${it.confirmed ? "line-through opacity-60" : ""}`}
                />
                <Input
                  value={it.note}
                  onChange={(e) => onPatchItem(it.id, { note: e.target.value })}
                  placeholder="הערה"
                  className={`bg-stone-900/70 border-white/10 ${theme.text} h-8 text-sm ${it.confirmed ? "opacity-60" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => onDeleteItem(it.id)}
                  className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/25 rounded p-1 transition"
                  aria-label="מחק פריט"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={onAddItem}
          className={`inline-flex items-center gap-1 rounded-lg border border-white/20 bg-black/25 hover:bg-black/40 ${theme.text} text-[11px] font-bold px-2 py-1 transition`}
          data-testid={`ff-schedule-add-item-${section.id}`}
        >
          <Plus className="h-3 w-3" />
          הוסף פריט
        </button>
      </div>

      <SendToSupplierDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        section={section}
        dayLabel={dayLabel}
        dayDate={dayDate}
        prodName={prodName}
      />
    </div>
  );
}

// ---- Send-to-supplier dialog — WhatsApp / mailto with pre-filled body ----
function SendToSupplierDialog({ open, onClose, section, dayLabel, dayDate, prodName }) {
  const message = useMemo(() => {
    if (!section) return "";
    const lines = [];
    if (prodName) lines.push(`הפקה: ${prodName}`);
    const dh = [dayLabel, dayDate].filter(Boolean).join(" · ");
    if (dh) lines.push(`יום: ${dh}`);
    lines.push(`קטגוריה: ${section.name || "—"}`);
    lines.push("");
    if ((section.items || []).length > 0) {
      lines.push("*רשימת פריטים:*");
      section.items.forEach((it, i) => {
        const q = it.quantity ? `${it.quantity} × ` : "";
        const note = it.note ? ` (${it.note})` : "";
        const mark = it.confirmed ? " ✅" : "";
        lines.push(`${i + 1}. ${q}${it.name || ""}${note}${mark}`);
      });
      lines.push("");
    }
    if (section.notes) {
      lines.push("*הערות:*");
      lines.push(section.notes);
    }
    return lines.join("\n");
  }, [section, dayLabel, dayDate, prodName]);

  const waPhone = section?.contact_whatsapp || section?.contact_phone || "";
  const email = section?.contact_email || "";
  const waLink = (() => {
    const d = (waPhone || "").replace(/\D/g, "");
    if (!d) return null;
    const intl = d.startsWith("0") ? `972${d.slice(1)}` : d;
    return `https://wa.me/${intl}?text=${encodeURIComponent(message)}`;
  })();
  const mailLink = email
    ? `mailto:${email}?subject=${encodeURIComponent((prodName ? `[${prodName}] ` : "") + (section?.name || "רשימת ציוד"))}&body=${encodeURIComponent(message)}`
    : null;

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("הועתק ללוח");
    } catch { toast.error("ההעתקה נכשלה"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`ff-schedule-send-dialog-${section?.id}`}>
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Send className="h-5 w-5 text-emerald-400" />
            שלח לספק · {section?.name || ""}
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            {section?.contact_name ? `איש קשר: ${section.contact_name}` : "לא הוגדר איש קשר לקטגוריה"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-1">
          <Textarea
            value={message}
            readOnly
            className="bg-stone-800 border-white/10 text-stone-100 text-sm min-h-[220px]"
            data-testid="ff-schedule-send-preview"
          />

          <div className="grid grid-cols-3 gap-2 pt-1">
            <a
              href={waLink || undefined}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => { if (!waLink) { e.preventDefault(); toast.error("אין מספר WhatsApp לאיש הקשר"); } }}
              className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-bold transition ${waLink ? "border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100" : "border-white/10 bg-stone-800/40 text-stone-500 cursor-not-allowed"}`}
              data-testid="ff-schedule-send-wa"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <a
              href={mailLink || undefined}
              onClick={(e) => { if (!mailLink) { e.preventDefault(); toast.error("אין מייל לאיש הקשר"); } }}
              className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-bold transition ${mailLink ? "border-fuchsia-500/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100" : "border-white/10 bg-stone-800/40 text-stone-500 cursor-not-allowed"}`}
              data-testid="ff-schedule-send-mail"
            >
              <Mail className="h-4 w-4" />
              מייל
            </a>
            <button
              type="button"
              onClick={copyText}
              className="inline-flex flex-col items-center justify-center gap-1 rounded-lg border border-sky-500/40 bg-sky-500/15 hover:bg-sky-500/25 text-sky-100 px-2 py-2 text-[11px] font-bold transition"
              data-testid="ff-schedule-send-copy"
            >
              <Clipboard className="h-4 w-4" />
              העתק
            </button>
          </div>

          <div className="flex items-center justify-end pt-1">
            <Button variant="outline" onClick={onClose} data-testid="ff-schedule-send-close">סגור</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Contact sub-popup ----------------------------------------------
function ContactPopup({ data, onClose, onSave }) {
  const [form, setForm] = useState({ contact_name: "", contact_phone: "", contact_whatsapp: "", contact_email: "" });

  useEffect(() => {
    if (!data) return;
    const s = data.sec || {};
    setForm({
      contact_name: s.contact_name || "",
      contact_phone: s.contact_phone || "",
      contact_whatsapp: s.contact_whatsapp || "",
      contact_email: s.contact_email || "",
    });
  }, [data]);

  const open = !!data;
  const tel = telHref(form.contact_phone);
  const wa = waHref(form.contact_whatsapp || form.contact_phone);
  const mail = mailHref(form.contact_email);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-md" data-testid="ff-schedule-contact-dialog">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <UserSquare className="h-4 w-4 text-emerald-400" />
            איש קשר · {data?.sec?.name || ""}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 pt-1">
          <Input
            placeholder="שם"
            value={form.contact_name}
            onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
            className="bg-stone-800 border-white/10 text-stone-100"
            data-testid="ff-schedule-contact-name"
          />
          <Input
            dir="ltr"
            placeholder="טלפון"
            value={form.contact_phone}
            onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
            className="bg-stone-800 border-white/10 text-stone-100 text-left"
            data-testid="ff-schedule-contact-phone"
          />
          <Input
            dir="ltr"
            placeholder="WhatsApp (אם שונה מהטלפון)"
            value={form.contact_whatsapp}
            onChange={(e) => setForm((f) => ({ ...f, contact_whatsapp: e.target.value }))}
            className="bg-stone-800 border-white/10 text-stone-100 text-left"
            data-testid="ff-schedule-contact-wa"
          />
          <Input
            dir="ltr"
            placeholder="Email"
            value={form.contact_email}
            onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
            className="bg-stone-800 border-white/10 text-stone-100 text-left"
            data-testid="ff-schedule-contact-email"
          />

          <div className="grid grid-cols-3 gap-2 pt-1">
            <a
              href={tel || undefined}
              onClick={(e) => { if (!tel) e.preventDefault(); }}
              className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-bold transition ${tel ? "border-sky-500/40 bg-sky-500/15 hover:bg-sky-500/25 text-sky-100" : "border-white/10 bg-stone-800/40 text-stone-500 cursor-not-allowed"}`}
              data-testid="ff-schedule-contact-call"
            >
              <Phone className="h-4 w-4" />
              חיוג
            </a>
            <a
              href={wa || undefined}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => { if (!wa) e.preventDefault(); }}
              className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-bold transition ${wa ? "border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100" : "border-white/10 bg-stone-800/40 text-stone-500 cursor-not-allowed"}`}
              data-testid="ff-schedule-contact-wa-link"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <a
              href={mail || undefined}
              onClick={(e) => { if (!mail) e.preventDefault(); }}
              className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-bold transition ${mail ? "border-fuchsia-500/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100" : "border-white/10 bg-stone-800/40 text-stone-500 cursor-not-allowed"}`}
              data-testid="ff-schedule-contact-mail-link"
            >
              <Mail className="h-4 w-4" />
              מייל
            </a>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="ff-schedule-contact-cancel">ביטול</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              onClick={() => onSave(form)}
              data-testid="ff-schedule-contact-save"
            >
              שמור
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
